import L from 'leaflet';

const ANIMATION_CLASS = 'cluster-zoom-anim';
const SPAWN_CLASS = 'cza-spawn';
const BURST_CLASS = 'cza-burst';
const CLEANUP_EXTRA_MS = 50;

class ClusterZoomAnimator {
    /**
     * Animates split and merge transitions of server-side clusters on zoom changes.
     * Queued operations are applied in one CSS transition frame by play(),
     * which toggles ANIMATION_CLASS on the map container so that marker icons
     * transition their transform and opacity.
     * @param {L.Map} map Leaflet map whose container receives the animation class.
     * @param {number} durationMs Must match the CSS transition duration.
     */
    constructor(map, durationMs = 420) {
        this.map = map;
        this.durationMs = durationMs;
        this.spawnOps = [];
        this.retireOps = [];
        this.fadeInMarkers = [];
        this.pendingRetire = [];
        this.decoratedIcons = [];
        this.cleanupTimer = null;
    }

    /**
     * Queues a newly created marker to fly out of its parent cluster position.
     * The marker is moved to the parent position now and animates to its own
     * position when play() runs.
     * @param {L.Marker} marker Marker not yet animated, positioned at its target.
     * @param {[number, number]} parentLatLng Parent cluster center.
     */
    spawnFromParent(marker, parentLatLng) {
        const target = marker.getLatLng();
        marker.setLatLng(L.latLng(parentLatLng));
        this.spawnOps.push({marker, target});
    }

    /**
     * Queues a newly created marker to fade in at its own position.
     * @param {L.Marker} marker Marker not yet added to the map.
     */
    fadeIn(marker) {
        marker.setOpacity(0);
        this.fadeInMarkers.push(marker);
    }

    /**
     * Queues a marker for animated removal, optionally collapsing into a target.
     * The marker fades out, flies to targetLatLng when given, and is removed
     * from layerGroup after the animation completes. Interaction is disabled
     * so the fading marker cannot swallow clicks aimed at its replacement.
     * @param {L.LayerGroup} layerGroup Group the marker will be removed from.
     * @param {L.Marker} marker Marker to retire.
     * @param {[number, number]|null} targetLatLng Merge target center or null.
     */
    retire(layerGroup, marker, targetLatLng = null) {
        marker['_czaRetiring'] = true;
        this.setMarkerInteractive(marker, false);
        this.retireOps.push({
            layerGroup,
            marker,
            origin: marker.getLatLng(),
            target: targetLatLng ? L.latLng(targetLatLng) : null,
        });
    }

    /**
     * Toggles pointer and keyboard interaction on the marker's icon element.
     * @param {L.Marker} marker Marker whose icon is toggled.
     * @param {boolean} interactive Whether the marker should accept input.
     */
    setMarkerInteractive(marker, interactive) {
        const element = marker.getElement ? marker.getElement() : null;
        if (!element) return;
        element.style.pointerEvents = interactive ? '' : 'none';
        element.setAttribute('tabindex', interactive ? '0' : '-1');
    }

    /**
     * Adds a droplet-effect CSS class to the marker's icon and tracks it
     * for removal when the animation finishes.
     * @param {L.Marker} marker Marker whose icon is decorated.
     * @param {string} className Effect class to add.
     */
    decorateIcon(marker, className) {
        const element = marker.getElement ? marker.getElement() : null;
        if (!element) return;
        element.classList.add(className);
        this.decoratedIcons.push({element, className});
    }

    /**
     * Returns true when the marker is queued or pending for animated removal.
     * @param {L.Marker} marker Marker to check.
     */
    isRetiring(marker) {
        return !!marker['_czaRetiring'];
    }

    /**
     * Cancels the queued removal of a marker that reappeared in fresh data.
     * Restores the position a merge-retired marker was moved away from,
     * re-enables interaction and opacity.
     * @param {L.Marker} marker Marker to keep on the map.
     */
    resurrect(marker) {
        delete marker['_czaRetiring'];
        const op = this.retireOps.find(o => o.marker === marker)
            || this.pendingRetire.find(o => o.marker === marker);
        if (op && op.target) marker.setLatLng(op.origin);
        this.retireOps = this.retireOps.filter(o => o.marker !== marker);
        this.pendingRetire = this.pendingRetire.filter(o => o.marker !== marker);
        marker.setOpacity(1);
        this.setMarkerInteractive(marker, true);
    }

    /**
     * Plays all queued operations as one CSS transition frame.
     * A previous unfinished run is completed first, then the animation class
     * is applied, a reflow is forced and target positions and opacities are
     * set so the browser transitions them.
     */
    play() {
        if (!this.spawnOps.length && !this.retireOps.length && !this.fadeInMarkers.length) return;
        this.finish();
        const container = this.map.getContainer();
        const spawnOps = this.spawnOps;
        const retireOps = this.retireOps;
        const fadeInMarkers = this.fadeInMarkers;
        this.spawnOps = [];
        this.retireOps = [];
        this.fadeInMarkers = [];
        container.classList.add(ANIMATION_CLASS);
        spawnOps.forEach(op => this.decorateIcon(op.marker, SPAWN_CLASS));
        fadeInMarkers.forEach(marker => this.decorateIcon(marker, SPAWN_CLASS));
        retireOps.forEach(op => {
            if (!op.target) this.decorateIcon(op.marker, BURST_CLASS);
        });
        void container.offsetWidth;
        spawnOps.forEach(op => op.marker.setLatLng(op.target));
        fadeInMarkers.forEach(marker => marker.setOpacity(1));
        retireOps.forEach(op => {
            if (op.target) op.marker.setLatLng(op.target);
            op.marker.setOpacity(0);
        });
        this.pendingRetire = retireOps;
        this.cleanupTimer = setTimeout(() => this.finish(), this.durationMs + CLEANUP_EXTRA_MS);
    }

    /**
     * Completes the current animation immediately: removes the animation class
     * and drops retired markers from their layer groups.
     */
    finish() {
        if (this.cleanupTimer) {
            clearTimeout(this.cleanupTimer);
            this.cleanupTimer = null;
        }
        this.map.getContainer().classList.remove(ANIMATION_CLASS);
        this.decoratedIcons.forEach(entry => entry.element.classList.remove(entry.className));
        this.decoratedIcons = [];
        this.pendingRetire.forEach(op => {
            delete op.marker['_czaRetiring'];
            op.layerGroup.removeLayer(op.marker);
        });
        this.pendingRetire = [];
    }
}

export default ClusterZoomAnimator;
